import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import { useAuth } from "../hooks/useAuth";
import { defaultHomePath } from "../utils/navigationScopes";
import { extractScopes } from "../utils/authStorage";

type MfaPhase = "loading" | "setup" | "challenge" | "done";

interface MfaStatus {
  required: boolean;
  enrolled: boolean;
  phase: string;
}

export default function MfaGate() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<MfaPhase>("loading");
  const [qrBase64, setQrBase64] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resolvePostMfaPath = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ onboarding_required: boolean }>(
        "/user/onboarding/status",
      );
      if (data?.onboarding_required) {
        navigate("/onboarding", { replace: true });
        return;
      }
    } catch {
      // Fall back to default-home behavior when status check fails.
    }

    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1])) as Record<
          string,
          unknown
        >;
        navigate(defaultHomePath(extractScopes(payload)), { replace: true });
        return;
      } catch {
        /* fall through */
      }
    }
    navigate("/cashflow", { replace: true });
  }, [navigate]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get<MfaStatus>("/auth/mfa/status");
      if (!data.required && data.enrolled) {
        void resolvePostMfaPath();
        return;
      }
      if (!data.required && !data.enrolled) {
        void resolvePostMfaPath();
        return;
      }
      if (data.phase === "verified") {
        void resolvePostMfaPath();
        return;
      }
      setPhase(data.enrolled ? "challenge" : "setup");
    } catch {
      setPhase("setup");
    }
  }, [resolvePostMfaPath]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const startSetup = useCallback(async () => {
    try {
      const { data } = await apiClient.post("/auth/mfa/setup/start");
      setQrBase64(data.qr_code_base64);
      setOtpauthUri(data.otpauth_uri);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail?.message || "Failed to start MFA setup",
      );
    }
  }, []);

  useEffect(() => {
    if (phase === "setup" && !qrBase64) {
      startSetup();
    }
  }, [phase, qrBase64, startSetup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const endpoint =
      phase === "setup" ? "/auth/mfa/setup/confirm" : "/auth/mfa/login/verify";

    try {
      const { data } = await apiClient.post(endpoint, { code });
      if (data.access_token) {
        await login(data.access_token);
        await resolvePostMfaPath();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail?.message;
      setError(msg || "Verification failed. Please try again.");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-emerald-50 via-teal-50 to-cyan-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border-2 border-emerald-200 p-6 md:p-8">
          <div className="flex flex-col items-center mb-6">
            <img
              src="/logo.jpeg"
              className="rounded-xl object-cover h-16 w-44 mb-4"
              alt="Datamint logo"
            />
            <h1 className="text-xl font-bold text-emerald-700">
              {phase === "setup"
                ? "Set Up Two-Factor Authentication"
                : "Two-Factor Verification"}
            </h1>
            <p className="text-slate-500 text-sm text-center mt-1">
              {phase === "setup"
                ? "Your organization requires MFA. Please set it up to continue."
                : "Enter the 6-digit code from your authenticator app."}
            </p>
          </div>

          {phase === "setup" && qrBase64 && (
            <div className="flex flex-col items-center mb-6">
              <p className="text-sm text-slate-600 mb-3">
                Scan this QR code with Google Authenticator or a compatible app:
              </p>
              <img
                src={`data:image/png;base64,${qrBase64}`}
                alt="TOTP QR Code"
                className="w-48 h-48 border rounded-lg"
              />
              {otpauthUri && (
                <details className="mt-3 w-full">
                  <summary className="text-xs text-slate-400 cursor-pointer">
                    Can't scan? Show manual key
                  </summary>
                  <code className="block mt-1 text-xs text-slate-500 break-all bg-slate-50 rounded p-2">
                    {otpauthUri}
                  </code>
                </details>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="mfa-code"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Verification Code
              </label>
              <input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(v);
                }}
                placeholder="000000"
                className="w-full rounded-lg border-2 border-emerald-200 px-4 py-2.5 text-center text-2xl tracking-[0.5em] font-mono focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                disabled={submitting}
              />
            </div>

            {error && (
              <p className="text-red-600 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={code.length !== 6 || submitting}
              className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold py-2.5 transition-colors"
            >
              {submitting ? "Verifying..." : "Verify"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={logout}
              className="text-sm text-slate-400 hover:text-slate-600 underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
